import { forwardRef, ComponentProps, Ref } from "react";

interface SectionProps extends ComponentProps<"div"> {
  containerClassName?: string;
  columnsClassName?: string;
  disableColumns?: boolean;
  disableContainer?: boolean;
  children: React.ReactNode;
  disableClip?: boolean;
  frameless?: boolean;
  tag?: "section" | "div" | "header";
  disable?: boolean;
}

/**
 * Standard Section includes 'section', 'container' and 'columns' class.
 * Use with property 'disableColumns' to avoid columns class.
 * Use with property 'disableContainer' to avoid 'container' class.
 * Always set 'sectionClassName' to enable specific styling.
 */
export const Section = forwardRef(
  (
    {
      columnsClassName,
      containerClassName,
      disableColumns,
      disableContainer,
      children,
      disableClip = false,
      frameless,
      className,
      tag = "section",
      disable = false,
      ...props
    }: SectionProps,
    ref: Ref<HTMLDivElement>, // Updated to support both tags
  ) => {
    if (disable) {
      return children;
    }
    if (frameless) {
      className = `${className} px-0`;
      if (disableContainer === false) {
        throw new Error(
          `"disableContainer" must not be set or set to true when "frameless" is set to true`,
        );
      }
      disableContainer = true;
    }
    let sectionJSX;

    if (disableContainer) {
      sectionJSX = disableColumns ? (
        children
      ) : (
        <div className={`columns ${columnsClassName ?? ""}`}>{children}</div>
      );
    } else {
      sectionJSX = (
        <div className={`container ${containerClassName ?? ""}`}>
          {disableColumns ? (
            children
          ) : (
            <div className={`columns ${columnsClassName ?? ""}`}>
              {children}
            </div>
          )}
        </div>
      );
    }

    //To show id if it is passed as prop only
    const { id, ...otherProps } = props;
    const Tag = tag;
    return (
      <Tag
        ref={ref}
        {...(id ? { id } : {})}
        {...otherProps}
        className={`section ${className} ${disableClip ? "" : "is-clipped"} `}
      >
        {sectionJSX}
      </Tag>
    );
  },
);
Section.displayName = "Section";
